/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

/**
 * Pure pricing helpers for the Managed MLOps catalog. Extracted out of
 * ManagedMLOpsCatalog.tsx so the tier + storage total computation can be
 * unit-tested independently of React.
 */

export interface StorageBounds {
  min_storage_gb: number;
  max_storage_gb: number;
}

export interface MonthlyPriceInputs {
  monthly_price: number;
  storage_price_per_gb_month: number;
}

/**
 * Clamps a requested storage size (GB) into the offering's [min, max] range.
 * Falls back to min_storage_gb when the input is not a finite number (e.g. the
 * storage input was cleared, producing NaN).
 */
export function clampStorage(offering: StorageBounds, gb: number): number {
  const safeGb = Number.isFinite(gb) ? gb : offering.min_storage_gb;
  return Math.min(Math.max(safeGb, offering.min_storage_gb), offering.max_storage_gb);
}

/**
 * Computes the total estimated monthly cost (minor currency units) for an
 * offering at a given storage size: base monthly price + storageGB * per-GB
 * storage rate. storageGB is NOT clamped here - callers that need clamped
 * totals should clamp first via clampStorage().
 */
export function computeMonthlyTotal(offering: MonthlyPriceInputs, storageGB: number): number {
  const safeStorageGB = Number.isFinite(storageGB) ? storageGB : 0;
  return offering.monthly_price + safeStorageGB * offering.storage_price_per_gb_month;
}
